import React, { useState, useEffect } from 'react';
import { Send, Eye, Calendar, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import AIGenerateButton from '../components/ai/AIGenerateButton';
import AIGenerationModal from '../components/ai/AIGenerationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublishPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [content, setContent] = useState('');
  const [platforms, setPlatforms] = useState({
    facebook: false,
    instagram: false,
    linkedin: false,
    x: false,
    tiktok: false,
    youtube: false
  });
  const [scheduling, setScheduling] = useState('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  const workspaceId = 'ws_demo';

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/products?status=active`
      );
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handlePlatformToggle = (platform) => {
    setPlatforms(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p]);

  const handlePublish = async () => {
    if (!selectedProduct || !content || selectedPlatforms.length === 0) {
      alert('Veuillez sélectionner un produit, rédiger du contenu et choisir au moins une plateforme');
      return;
    }

    setPublishing(true);
    try {
      // Create draft
      const createResponse = await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/publications`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_id: selectedProduct.id,
            content,
            platforms: selectedPlatforms,
            media_ids: selectedProduct.images || [],
            scheduled_at: scheduling === 'scheduled' ? scheduledDate : null
          })
        }
      );
      const draft = await createResponse.json();

      // Publish
      await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/publications/${draft.id}/publish`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduled_at: scheduling === 'scheduled' ? scheduledDate : null
          })
        }
      );

      alert('Publication créée avec succès !');
      window.location.href = '/history';
    } catch (error) {
      console.error('Publication failed:', error);
      alert('Échec de la publication');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="publish-page-title">Créer une Publication</h1>
        <p className="text-slate-600 mb-8">Sélectionnez un produit et publiez sur vos réseaux sociaux</p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Product Selection */}
            <Card data-testid="product-selection-card">
              <CardHeader>
                <CardTitle>1. Sélectionner un Produit</CardTitle>
              </CardHeader>
              <CardContent>
                <select
                  value={selectedProduct?.id || ''}
                  onChange={(e) => {
                    const product = products.find(p => p.id === e.target.value);
                    setSelectedProduct(product);
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md"
                  data-testid="product-selector"
                >
                  <option value="">-- Choisir un produit --</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
                {selectedProduct && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-md">
                    <p className="font-medium">{selectedProduct.name}</p>
                    <p className="text-sm text-slate-600">{selectedProduct.price} {selectedProduct.currency}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Content */}
            <Card data-testid="content-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>2. Rédiger le Contenu</CardTitle>
                  {selectedProduct && selectedPlatforms.length > 0 && (
                    <AIGenerateButton
                      productId={selectedProduct.id}
                      platforms={selectedPlatforms}
                      onGenerate={() => setShowAIModal(true)}
                      size="sm"
                      variant="outline"
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Rédigez votre publication... (max 3000 caractères)"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={8}
                  maxLength={3000}
                  data-testid="content-textarea"
                />
                <p className="text-sm text-slate-500 mt-2">
                  {content.length} / 3000 caractères
                </p>
              </CardContent>
            </Card>

            {/* Platforms */}
            <Card data-testid="platforms-card">
              <CardHeader>
                <CardTitle>3. Sélectionner les Plateformes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['facebook', 'instagram', 'linkedin', 'x', 'tiktok', 'youtube'].map(platform => (
                    <label key={platform} className="flex items-center gap-3 cursor-pointer" data-testid={`platform-${platform}`}>
                      <Checkbox
                        checked={platforms[platform]}
                        onCheckedChange={() => handlePlatformToggle(platform)}
                      />
                      <span className="flex items-center gap-2">
                        <span className="capitalize font-medium">
                          {platform === 'x' ? 'X (Twitter)' : platform === 'youtube' ? 'YouTube' : platform === 'tiktok' ? 'TikTok' : platform}
                        </span>
                        {platforms[platform] && <Check size={16} className="text-green-600" />}
                      </span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Scheduling */}
            <Card data-testid="scheduling-card">
              <CardHeader>
                <CardTitle>4. Planification</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="scheduling"
                      value="immediate"
                      checked={scheduling === 'immediate'}
                      onChange={(e) => setScheduling(e.target.value)}
                      data-testid="scheduling-immediate"
                    />
                    <span>Publier immédiatement</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="scheduling"
                      value="scheduled"
                      checked={scheduling === 'scheduled'}
                      onChange={(e) => setScheduling(e.target.value)}
                      data-testid="scheduling-scheduled"
                    />
                    <span>Programmer pour plus tard</span>
                  </label>
                  {scheduling === 'scheduled' && (
                    <input
                      type="datetime-local"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-md mt-2"
                      data-testid="scheduled-datetime"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={() => setShowPreview(true)}
                variant="outline"
                className="w-full"
                disabled={!selectedProduct || !content || selectedPlatforms.length === 0}
                data-testid="preview-btn"
              >
                <Eye size={20} className="mr-2" />
                Prévisualiser
              </Button>
              <Button
                onClick={handlePublish}
                className="w-full"
                disabled={publishing || !selectedProduct || !content || selectedPlatforms.length === 0}
                data-testid="publish-btn"
              >
                {publishing ? (
                  <>Publi cation en cours...</>
                ) : scheduling === 'scheduled' ? (
                  <><Calendar size={20} className="mr-2" />Programmer</>
                ) : (
                  <><Send size={20} className="mr-2" />Publier Maintenant</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Modal Placeholder */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="preview-modal">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Prévisualisation Multi-plateformes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">Aperçu pour {selectedPlatforms.join(', ')}</p>
                <div className="space-y-4">
                  {selectedPlatforms.map(platform => (
                    <div key={platform} className="border border-slate-200 rounded-md p-4">
                      <h3 className="font-bold capitalize mb-2">{platform}</h3>
                      <p className="text-sm">{content}</p>
                    </div>
                  ))}
                </div>
                <Button className="mt-4" onClick={() => setShowPreview(false)}>Fermer</Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Generation Modal */}
        {selectedProduct && (
          <AIGenerationModal
            isOpen={showAIModal}
            onClose={() => setShowAIModal(false)}
            product={selectedProduct}
            workspaceId={workspaceId}
            onGenerated={(variant) => {
              // Update content with generated text
              setContent(variant.text + '\n\n' + variant.hashtags.join(' '));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PublishPage;
